// Type guard for AgentHealthPanelMessage (for UI rendering only)
export function isAgentHealthPanelMessage(message: unknown): message is AgentHealthPanelMessage {
  return (
    typeof message === 'object' &&
    message !== null &&
    'renderType' in message &&
    (message as any).renderType === 'agent_health' &&
    'content' in message &&
    typeof (message as any).content === 'object' &&
    (message as any).content !== null &&
    'jobMetrics' in (message as any).content &&
    'agentHealth' in (message as any).content
  );
}
import { Message as AIMessage } from 'ai';
import type { JobMetrics } from '../components/JobMetricsPanel';
import type { AgentHealth } from '../components/AgentHealthCard';
import type { Entity } from './entity';
import type { Widget } from './widget';

export type MessageRole = 'user' | 'assistant' | 'system' | 'function' | 'tool';

export type MessageStatus =
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'error'
  | 'streaming';

export type MessageRenderType =
  | 'text'
  | 'markdown'
  | 'status'
  | 'card'
  | 'document'
  | 'function_call'
  | 'partial_response'
  | 'error'
  | 'follow_up'
  | 'clarification'
  | 'agent_health';

export interface AgentHealthPanelContent {
  jobMetrics: JobMetrics;
  agentHealth: AgentHealth;
  // LexAgent integration fields
  clarification: import('@/types/lexAgent').LexAgentClarification | null;
  actions: import('@/types/lexAgent').LexAgentAction[];
  agentState: import('@/types/lexAgent').LexAgentState | null;
  plugins: import('@/types/lexAgent').LexAgentPlugin[];
  telemetry: import('@/types/lexAgent').LexAgentTelemetry | null;
  cost: import('@/types/lexAgent').LexAgentCost | null;
}

export interface AgentHealthPanelMessage extends Omit<MessageBase, 'content' | 'renderType'> {
  content: AgentHealthPanelContent;
  renderType: 'agent_health';
}

export interface AgenticEvaluationMetadata {
  enhanced?: boolean;
  insightCount?: number;
  additionalActionsCount?: number;
  confidence?: number;
  supportedClaimsCount?: number;
}

export interface PersonaTelemetryMetadata {
  styleMode?: 'brief' | 'balanced' | 'detailed' | string;
  tone?: 'concise' | 'conversational' | string;
  guardDowngraded?: boolean;
  guardReason?: string;
}

export interface ChatMessageMetadata extends Record<string, unknown> {
  agenticEvaluation?: AgenticEvaluationMetadata;
  persona?: PersonaTelemetryMetadata;
  /** Pipeline metadata for feedback classification */
  intentCategory?: string;
  toolId?: string;
  toolSuccess?: boolean;
}

// Remove duplicate ChatMessage export below

/**
 * Generic base message — overrides 'role' from default AI message
 */
export interface MessageBase extends Omit<AIMessage, 'role'> {
  id: string;
  role: MessageRole;
  content: string;
  name?: string;
  createdAt?: Date;
  updatedAt?: Date;
  status?: MessageStatus;
  renderType?: MessageRenderType;
  metadata?: ChatMessageMetadata;
  error?: string;
  intentCategory?: string;
  streamId?: string;
  // Entity highlighting support
  entities?: Entity[];
  // Inline workflow widgets
  widgets?: Widget[];
}

export type CardType =
  | 'calendar'
  | 'document'
  | 'task'
  | 'billing'
  | 'info'
  | 'search_results'
  | 'media'
  | 'contact'
  | 'timeline'
  | 'summary'
  | 'code'
  | 'discovery'
  | 'checklist'
  | 'onboarding'
  | 'legal_hold';

export interface CardContent {
  type: CardType;
  title: string;
  description?: string;
  data?: Record<string, unknown>;
  actions?: {
    label: string;
    action: 'confirm' | 'reschedule' | 'cancel' | 'open' | 'edit';
    payload?: any;
  }[];
}

export interface CardMessage extends Omit<MessageBase, 'content' | 'role'> {
  role: 'assistant' | 'system';
  content: CardContent;
  cardType: CardType;
  renderType: 'card';
}

export interface DocumentContent {
  document_id: string;
  version_id?: string; // Version ID of the latest/current version
  title: string;
  originalName?: string;
  size?: number;
  mimeType?: string;
  uploadedAt?: Date;
  url?: string;
  metadata?: Record<string, unknown>;
  actions?: {
    label: string;
    action: 'view' | 'download' | 'extract_metadata' | 'analyze';
    payload?: any;
  }[];
}

export interface DocumentMessage extends Omit<MessageBase, 'content' | 'renderType'> {
  content: DocumentContent;
  renderType: 'document';
}

export type ChatMessage = MessageBase | CardMessage | DocumentMessage;

export interface Conversation {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
  metadata?: Record<string, unknown>;
  isArchived?: boolean;
}

export interface ChatState {
  currentConversationId: string | null;
  conversations: Record<string, Conversation>;

  // UI
  isSidebarOpen: boolean;
  isLoading: boolean;
  error: string | null;

  // Intention summary state
  currentIntent: string | null;
  intentConfidence: number | null;
  fallbackStrategy: string | null;
  followUpQueue: string[];
  setIntentionSummary: (
    intent: string | null,
    confidence: number | null,
    fallback: string | null,
    followUps: string[]
  ) => void;

  // LexAgent integration fields and setters
  clarification: import('@/types/lexAgent').LexAgentClarification | null;
  setClarification: (clarification: import('@/types/lexAgent').LexAgentClarification | null) => void;
  actions: import('@/types/lexAgent').LexAgentAction[];
  setActions: (actions: import('@/types/lexAgent').LexAgentAction[]) => void;
  agentState: import('@/types/lexAgent').LexAgentState | null;
  setAgentState: (agentState: import('@/types/lexAgent').LexAgentState | null) => void;
  plugins: import('@/types/lexAgent').LexAgentPlugin[];
  setPlugins: (plugins: import('@/types/lexAgent').LexAgentPlugin[]) => void;
  telemetry: import('@/types/lexAgent').LexAgentTelemetry | null;
  setTelemetry: (telemetry: import('@/types/lexAgent').LexAgentTelemetry | null) => void;
  cost: import('@/types/lexAgent').LexAgentCost | null;
  setCost: (cost: import('@/types/lexAgent').LexAgentCost | null) => void;

  // Plugin selection state
  activePluginIds: string[];
  setActivePluginIds: (ids: string[]) => void;
  togglePlugin: (pluginId: string) => void;

  // Conversation actions
  createConversation: (title?: string, preferredId?: string) => string;
  deleteConversation: (id: string) => void;
  setCurrentConversation: (id: string) => void;
  clearConversation: (conversationId?: string) => void;

  // Message actions
  addMessage: (
    message: Omit<ChatMessage, 'id' | 'createdAt' | 'updatedAt'>,
    conversationId?: string
  ) => string;
  updateMessage: (
    messageId: string,
    updates: Partial<ChatMessage>,
    conversationId?: string
  ) => void;
  deleteMessage: (messageId: string, conversationId?: string) => void;

  // Card-specific actions
  sendCardMessage: (
    cardType: CardType,
    content: Omit<CardContent, 'type'>,
    conversationId?: string
  ) => string;
  updateCardContent: (
    messageId: string,
    updates: Partial<CardContent>,
    conversationId?: string
  ) => void;

  // Persistence
  loadConversations: () => Promise<void>;
  saveConversations: () => Promise<void>;
}

// Type guards
export function isCardMessage(message: ChatMessage): message is CardMessage {
  return message.renderType === 'card' && 'cardType' in message;
}

export function isDocumentMessage(message: ChatMessage): message is DocumentMessage {
  return message.renderType === 'document';
}

export function isTextMessage(message: ChatMessage): message is MessageBase {
  return !isCardMessage(message) && !isDocumentMessage(message);
}
